/* ============================================================================
   SHARE STANDING — the account as it stands, to the client, in one tap.

   WhatsApp opens a message with the summary and the portal link; Email sends
   the same as a document of record (client and studio, outcome kept on the
   project); the link and the summary can also be copied for anywhere else.
   ========================================================================== */
import { useState } from "react";
import { Icon } from "./Icons";
import { actions, money } from "../lib/store";
import { standingText, standingLink } from "../lib/billing";
import { openWhatsApp } from "../lib/whatsapp";
import { latestRecord, recordState } from "./RecordStatus";
import { fmtDate } from "../lib/format";
import type { Project } from "../lib/types";

export function ShareStanding({ p, by }: { p: Project; by: string }) {
  const [copied, setCopied] = useState<"link" | "summary" | null>(null);
  const [open, setOpen] = useState(false);
  const cur = p.billing?.currency ?? p.currency;
  const text = standingText(p, (n) => money(n, cur));
  const link = standingLink(p);
  const rec = latestRecord(p, "statement-issued");
  const st = recordState(rec);
  const copy = async (what: "link" | "summary") => {
    try { await navigator.clipboard.writeText(what === "link" ? link : text); setCopied(what); setTimeout(() => setCopied(null), 1800); } catch { /* clipboard blocked */ }
  };
  return (
    <span className="row wrap" style={{ gap: 6, alignItems: "center" }}>
      <button className="btn sm" onClick={() => openWhatsApp(text, p.client.phone)} title={p.client.phone ? `WhatsApp ${p.client.phone}` : "WhatsApp — pick the contact"}>
        <Icon.whatsapp width={14} style={{ color: "#25D366" }} />WhatsApp standing
      </button>
      <button className="btn sm" disabled={st.sending} onClick={() => actions.record(p.id, { kind: "statement-issued", title: `Account standing — ${fmtDate(new Date().toISOString())}`, by })} title={p.client.email ? `Email ${p.client.email}, studio in copy` : "No client email on the project"}>
        <Icon.send width={13} />{st.sending ? "Sending…" : "Email standing"}
      </button>
      <button className="btn ghost sm" onClick={() => void copy("link")}><Icon.link width={13} />{copied === "link" ? "Link copied" : "Copy link"}</button>
      <button className="btn ghost sm" onClick={() => void copy("summary")}><Icon.copy width={13} />{copied === "summary" ? "Summary copied" : "Copy summary"}</button>
      <button className="btn ghost sm" onClick={() => setOpen((v) => !v)} title="See what gets sent">{open ? "Hide" : "Preview"}</button>
      {rec && <span className={`badge ${st.tone}`} style={{ fontSize: 9.5 }} title={rec.recipients.map((r) => `${r.party}: ${r.to} — ${r.result}${r.error ? ` (${r.error})` : ""}`).join("\n")}>{st.label}{rec.on ? ` · ${fmtDate(rec.on)}` : ""}</span>}
      {open && (
        <pre className="panel" style={{ width: "100%", margin: "6px 0 0", padding: "10px 14px", fontSize: 12, lineHeight: 1.5, whiteSpace: "pre-wrap", fontFamily: "inherit" }}>{text}</pre>
      )}
    </span>
  );
}
